import type { ReactNode } from 'react';
import { Badge } from '@/components/shadcn/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn/card';
import type { IApplicationDetails } from '@/features/ApplicationForm/types';
import type { GmfCompletedProject, GmfDeveloperPipelineFormValues, GmfProjectFile } from './types/gmfDeveloperPipeline.types';
import { parseMetricNumber } from './utils/dashboardMetrics';
import { formatCurrency, formatNumber } from './utils/formatters';

const DetailRow = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div className='grid gap-1 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0 sm:grid-cols-3'>
    <dt className='font-medium text-slate-600'>{label}</dt>
    <dd className='sm:col-span-2'>{value || 'Not provided'}</dd>
  </div>
);

const SectionCard = ({ title, children }: { title: string; children: ReactNode }) => (
  <Card className='mb-5 rounded-md'>
    <CardHeader>
      <CardTitle className='text-lg'>{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <dl className='overflow-hidden rounded-md border border-slate-200'>{children}</dl>
    </CardContent>
  </Card>
);

export const GMFDeveloperPipelineApplicationDetails = ({ applicationData, files }: IApplicationDetails) => {
  const data = applicationData as GmfDeveloperPipelineFormValues;
  const project = data.projectDetails;
  const applicant = data.applicantInformation;
  const uploadedFiles = (files ?? []) as Array<GmfProjectFile & { url?: string }>;

  return (
    <div className='space-y-5'>
      <SectionCard title='Project Details'>
        <DetailRow label='Street Address' value={project.streetAddress} />
        <DetailRow label='City' value={project.city} />
        <DetailRow label='State' value={project.state} />
        <DetailRow label='Deal Type' value={project.dealType} />
        <DetailRow label='ZIP / Postal Code' value={project.zipCode} />
        <DetailRow label='Proposed Units' value={formatNumber(parseMetricNumber(project.proposedUnits))} />
        <DetailRow label='By-Right' value={project.projectByRight} />
        <DetailRow label='Construction Type' value={project.constructionType} />
        <DetailRow label='Acquisition Type' value={project.acquisitionType} />
        <DetailRow label='Site Control Status' value={project.siteControlStatus} />
        <DetailRow label='Estimated Acquisition Cost' value={formatCurrency(parseMetricNumber(project.acquisitionCostEstimate))} />
        <DetailRow label='Estimated Development Cost' value={formatCurrency(parseMetricNumber(project.developmentCostEstimate))} />
        <DetailRow label='Estimated Predevelopment Cost' value={formatCurrency(parseMetricNumber(project.preDevelopmentCostEstimate))} />
        <DetailRow label='Community Need' value={project.communityNeed} />
        <DetailRow label='Target Population' value={project.targetPopulation} />
        <DetailRow label='Preliminary Financing Strategy' value={project.preliminaryFinancingStrategy} />
        <DetailRow label='Preliminary Development Timeline' value={project.preliminaryDevelopmentTimeline} />
        <DetailRow label='Key Partners' value={project.keyPartners} />
      </SectionCard>

      <SectionCard title='Applicant Information'>
        <DetailRow label='Entity Name' value={applicant.entityName} />
        <DetailRow label='Business Entity State' value={applicant.businessEntityState} />
        <DetailRow label='Website' value={applicant.website} />
        <DetailRow label='Formation Date' value={applicant.formationDate} />
        <DetailRow
          label='Business Address'
          value={`${applicant.businessStreetAddress}, ${applicant.businessCity}, ${applicant.businessState} ${applicant.businessZipCode}`}
        />
        <DetailRow label='Contact Name' value={applicant.contactPersonName} />
        <DetailRow label='Contact Email' value={applicant.contactPersonEmail} />
        <DetailRow label='Phone' value={applicant.phone} />
        <DetailRow label='Date of Birth' value={applicant.dateOfBirth} />
        <DetailRow label='ACRE Graduate' value={applicant.acreGraduate} />
        <DetailRow label='Organization Type' value={applicant.organizationType} />
        <DetailRow label='Applicant Identity' value={applicant.applicantIdentity?.join(', ')} />
        <DetailRow label='Education Background' value={applicant.educationBackground} />
        <DetailRow label='Professional Licenses' value={applicant.professionalLicenses} />
        <DetailRow label='Professional Training' value={applicant.professionalTrainingHistory?.join(', ')} />
        <DetailRow label='Real Estate Entities' value={applicant.realEstateEntities} />
        <DetailRow label='Current Employer and Role' value={applicant.currentEmployerRole} />
      </SectionCard>

      <ProjectExperience title='Company Completed Projects' projects={data.experience.companyCompleted} />
      <ProjectExperience title='Principal Completed Projects' projects={data.experience.principalCompleted} />

      <Card className='rounded-md'>
        <CardHeader>
          <CardTitle className='text-lg'>Attachments</CardTitle>
        </CardHeader>
        <CardContent>
          {uploadedFiles.length === 0 ? (
            <p className='text-sm text-slate-500'>No additional documents were uploaded.</p>
          ) : (
            <div className='flex flex-wrap gap-2'>
              {uploadedFiles.map((file) => (
                <a
                  key={file.key}
                  href={file.url}
                  target='_blank'
                  rel='noreferrer'
                  className='cursor-pointer rounded-md border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50'
                >
                  {file.name}
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const ProjectExperience = ({ title, projects }: { title: string; projects: GmfCompletedProject[] }) => (
  <Card className='mb-5 rounded-md'>
    <CardHeader>
      <CardTitle className='text-lg'>{title}</CardTitle>
    </CardHeader>
    <CardContent>
      {projects.length === 0 ? (
        <p className='text-sm text-slate-500'>No projects provided.</p>
      ) : (
        <div className='grid gap-3 md:grid-cols-2'>
          {projects.map((project, index) => (
            <div key={`${project.projectName}-${index}`} className='rounded-md border border-slate-200 p-4'>
              <div className='mb-2 flex items-center justify-between gap-3'>
                <h4 className='font-medium text-slate-900'>{project.projectName}</h4>
                <Badge variant='outline'>{project.units} units</Badge>
              </div>
              <p className='text-sm text-slate-600'>{project.address}</p>
              <p className='mt-2 text-sm text-slate-600'>Role: {project.role}</p>
              <p className='text-sm text-slate-600'>Completed: {project.completionYear}</p>
            </div>
          ))}
        </div>
      )}
    </CardContent>
  </Card>
);
